/**
 * SessionPersistence — IndexedDB persistence helpers for the chat widget.
 *
 * Encapsulates session save/load, payload caching, favorite toggling,
 * and URL navigation with pre-save.
 *
 * Extracted from chat/index.ts to improve cohesion and reduce file size.
 */

import type { GengageIndexedDB, FavoriteData } from '../common/indexed-db.js';
import type { CommunicationBridge } from '../common/communication-bridge.js';
import { isSafeUrl } from '../common/safe-html.js';
import { navigateToUrl } from '../common/navigation.js';
import type { BackendContext, UISpec } from '../common/types.js';
import type { ChatMessage, SerializableChatMessage } from './types.js';
import type { PanelSource } from './panel-manager.js';

export type { FavoriteData };

type NavigateFn = (url: string) => void;

/**
 * Write-amplification caps for the persisted session record.
 *
 * persist() runs after every stream completion and rewrites the whole session
 * record each time, so an unbounded message list means each save grows with the
 * conversation (O(n) per turn → O(n²) over a session). We keep only the most
 * recent slice: enough to restore a usable conversation without paying for the
 * full transcript on every write. Older turns remain in the localStorage chat
 * history (history-storage.ts), which is the durable archive.
 */
const MAX_PERSISTED_MESSAGES = 60;
/** Cap on panel snapshots persisted per session (each can be a sizeable HTML blob). */
const MAX_PERSISTED_PANEL_SNAPSHOTS = 30;

export interface PersistSessionParams {
  userId: string;
  appId: string;
  sessionId: string;
  messages: ChatMessage[];
  currentThreadId: string | null;
  lastThreadId: string | null;
  chatCreatedAt: string;
  panelSnapshots: Map<string, HTMLElement>;
  panelSnapshotTypes?: Map<string, string> | undefined;
  panelSnapshotSources?: ReadonlyMap<string, PanelSource> | undefined;
  panelThreads: string[];
  panelAiSpecByThread?: Map<string, UISpec> | undefined;
  lastBackendContext: BackendContext | null;
  sku?: string | undefined;
}

export class SessionPersistence {
  private _db: GengageIndexedDB | null;
  /** Favorited product SKUs (loaded from IDB). */
  readonly favoritedSkus: Set<string> = new Set();
  /** Full favorite data cache (loaded from IDB alongside favoritedSkus). */
  private _favoritesCache: Map<string, FavoriteData> = new Map();
  /** Async mutex: serializes persist() calls to prevent interleaved IDB writes. */
  private _persistLock: Promise<void> = Promise.resolve();

  constructor(db: GengageIndexedDB | null) {
    this._db = db;
  }

  get db(): GengageIndexedDB | null {
    return this._db;
  }

  set db(value: GengageIndexedDB | null) {
    this._db = value;
  }

  /**
   * Persist current session state to IndexedDB.
   * Called after each stream completion (onDone). Non-fatal on failure.
   */
  async persist(params: PersistSessionParams): Promise<void> {
    if (!this._db) return;
    // Serialize through mutex to prevent interleaved IDB writes
    const prev = this._persistLock;
    let unlock: () => void;
    this._persistLock = new Promise<void>((r) => {
      unlock = r;
    });
    await prev;
    try {
      await this._persistImpl(params);
    } finally {
      unlock!();
    }
  }

  private async _persistImpl(params: PersistSessionParams): Promise<void> {
    if (!this._db) return;

    // Cap write amplification: persist only the most recent slice of the
    // conversation. Older turns stay in the localStorage chat-history archive.
    const recentMessages =
      params.messages.length > MAX_PERSISTED_MESSAGES
        ? params.messages.slice(-MAX_PERSISTED_MESSAGES)
        : params.messages;

    const serializableMessages: SerializableChatMessage[] = recentMessages.map((m) => {
      const sm: SerializableChatMessage = {
        id: m.id,
        role: m.role,
        timestamp: m.timestamp,
        status: m.status === 'streaming' ? 'done' : m.status,
      };
      if (m.threadId !== undefined) sm.threadId = m.threadId;
      if (m.content !== undefined) sm.content = m.content;
      if (m.frontendOnly) sm.frontendOnly = true;
      if (m.silent) sm.silent = true;
      return sm;
    });

    // Serialize panel snapshots to HTML strings (never persist loading skeletons).
    // Cap to the most recent snapshots — Maps are insertion-ordered, so the last
    // N entries are the newest. Each snapshot can be a sizeable HTML blob, so an
    // unbounded count is the dominant write-amplification cost.
    const snapshotEntries = [...params.panelSnapshots];
    const recentSnapshots =
      snapshotEntries.length > MAX_PERSISTED_PANEL_SNAPSHOTS
        ? snapshotEntries.slice(-MAX_PERSISTED_PANEL_SNAPSHOTS)
        : snapshotEntries;
    const panelSnapshotHtml: Record<string, string> = {};
    for (const [msgId, el] of recentSnapshots) {
      if (el.querySelector('.gengage-chat-panel-skeleton')) continue;
      panelSnapshotHtml[msgId] = el.innerHTML;
    }
    // Cap the metadata maps the same way as the HTML snapshots so they can't grow
    // without bound across a long session. Maps are insertion-ordered, so the last
    // N entries are the newest. (These are independent of the HTML-snapshot set —
    // a message can carry a type/source without a persisted HTML blob — so we cap
    // by recency rather than intersecting with the persisted HTML ids.)
    const capRecent = <V>(map: ReadonlyMap<string, V> | undefined): Record<string, V> => {
      const entries = map ? [...map] : [];
      const recent =
        entries.length > MAX_PERSISTED_PANEL_SNAPSHOTS ? entries.slice(-MAX_PERSISTED_PANEL_SNAPSHOTS) : entries;
      const out: Record<string, V> = {};
      for (const [key, value] of recent) out[key] = value;
      return out;
    };
    const panelSnapshotTypes = capRecent(params.panelSnapshotTypes);
    const panelSnapshotSources = capRecent(params.panelSnapshotSources);
    const panelAiSpecByThread = capRecent(params.panelAiSpecByThread);

    await this._db.saveSession({
      userId: params.userId,
      appId: params.appId,
      sessionId: params.sessionId,
      messages: serializableMessages,
      currentThreadId: params.currentThreadId,
      lastThreadId: params.lastThreadId,
      createdAt: params.chatCreatedAt,
      panelThreads: params.panelThreads.length > 0 ? params.panelThreads : undefined,
      panelSnapshotHtml: Object.keys(panelSnapshotHtml).length > 0 ? panelSnapshotHtml : undefined,
      panelSnapshotTypes: Object.keys(panelSnapshotTypes).length > 0 ? panelSnapshotTypes : undefined,
      panelSnapshotSources: Object.keys(panelSnapshotSources).length > 0 ? panelSnapshotSources : undefined,
      panelAiSpecByThread: Object.keys(panelAiSpecByThread).length > 0 ? panelAiSpecByThread : undefined,
      sku: params.sku,
    });

    // Save latest context snapshot for current thread
    if (params.lastBackendContext && params.currentThreadId) {
      await this._db.saveContext({
        sessionId: params.sessionId,
        threadId: params.currentThreadId,
        context: params.lastBackendContext,
      });
    }

    // Save UISpec payloads separately
    // Note: we intentionally do NOT delete m.uiSpec from the caller's live objects.
    // The caller's message array must remain intact for re-rendering and snapshot logic.
    // Scoped to the same recent slice persisted above so we don't write payloads
    // for messages that were dropped from the session record.
    for (const m of recentMessages) {
      if (m.uiSpec && m.threadId) {
        await this._db.savePayload({
          threadId: m.threadId,
          messageId: m.id,
          uiSpec: m.uiSpec,
        });
      }
    }
  }

  /**
   * Persist session to IndexedDB, then navigate to the URL.
   * Sends an 'openURLInNewTab' bridge message so the host page can intercept
   * (e.g. for SPA routing), then navigates directly as fallback.
   *
   * Legacy compatibility: the prior engine navigated via window.location.href
   * after posting saveSessionAndOpenURL to the iframe. The clean-room runs in
   * the same window (Shadow DOM, not iframe), so it navigates directly.
   */
  async saveAndOpenURL(
    url: string,
    persistFn: () => Promise<void>,
    bridge: CommunicationBridge | null,
    navigate: NavigateFn = (targetUrl) => {
      navigateToUrl(targetUrl);
    },
  ): Promise<void> {
    try {
      await persistFn();
    } catch {
      // Non-fatal — still navigate
    }
    // Guard first: an unsafe URL (e.g. javascript:) must reach neither the host
    // bridge nor the SDK navigation. Forwarding it to the host could let a
    // careless SPA route handler execute it.
    if (!isSafeUrl(url)) return;
    // Notify host so SPAs can intercept (e.g. router.push instead of full reload).
    // NOTE: The bridge message is fire-and-forget. The subsequent location.href
    // assignment may trigger a page unload before the host processes the message.
    // This matches legacy behavior. SPAs that need to intercept
    // should handle the bridge event synchronously or call event.preventDefault()
    // on the gengage:navigate CustomEvent to suppress the fallback navigation.
    bridge?.send('openURLInNewTab', { url });
    navigate(url);
  }

  /**
   * Load a UISpec payload from IndexedDB with retry logic.
   * Returns null if not found or all retries fail.
   */
  async loadPayload(threadId: string, messageId: string): Promise<UISpec | null> {
    if (!this._db) return null;

    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const payload = await this._db.loadPayload(threadId, messageId);
        if (payload) return payload.uiSpec;
      } catch {
        // Retry after delay
      }
      if (attempt < 2) {
        await new Promise((r) => setTimeout(r, 100));
      }
    }
    return null;
  }

  /**
   * Load favorited SKUs from IDB into the in-memory set.
   */
  async loadFavorites(userId: string, appId: string): Promise<void> {
    if (!this._db) return;
    try {
      const favs = await this._db.loadFavorites(userId, appId);
      for (const f of favs) {
        this.favoritedSkus.add(f.sku);
        this._favoritesCache.set(f.sku, f);
      }
    } catch {
      // Non-fatal — continue without favorites
    }
  }

  /** Returns favorited products ordered newest-first. */
  getFavoriteProducts(): FavoriteData[] {
    return [...this._favoritesCache.values()].sort((a, b) => {
      return (b.savedAt ?? '').localeCompare(a.savedAt ?? '');
    });
  }

  /**
   * Toggle a product's favorited state in IDB and in-memory set.
   */
  async toggleFavorite(userId: string, appId: string, sku: string, product: Record<string, unknown>): Promise<void> {
    // Always update in-memory state first — IDB is optional (persistence only)
    if (this.favoritedSkus.has(sku)) {
      this.favoritedSkus.delete(sku);
      this._favoritesCache.delete(sku);
      if (this._db) await this._db.removeFavorite(userId, appId, sku);
    } else {
      const data: FavoriteData = {
        userId,
        appId,
        sku,
        name: product['name'] as string | undefined,
        imageUrl: product['imageUrl'] as string | undefined,
        price: product['price'] as string | undefined,
        savedAt: new Date().toISOString(),
      };
      this.favoritedSkus.add(sku);
      this._favoritesCache.set(sku, data);
      if (this._db) await this._db.saveFavorite(data);
    }
  }

  close(): void {
    this._db?.close();
    this._db = null;
  }
}
